import { z } from 'zod';
import {
  AbilityScoreSchema,
  type AbilityScore,
  DamageTypeSchema,
  type DamageType,
  DiceExpressionSchema,
  type DiceExpression,
  MovementModeSchema,
  type MovementMode,
  ProficiencyLevelSchema,
  type ProficiencyLevel,
  RechargeSchema,
  type Recharge,
  SenseSchema,
  type Sense,
  SkillSchema,
  type Skill,
  SpellLevelSchema,
  type SpellLevel,
  WeaponMasterySchema,
  type WeaponMastery,
} from './primitives.js';
import { FormulaSchema, type Formula } from './formula.js';
import { PredicateSchema, type Predicate } from './predicate.js';

export type ModifierTarget =
  | 'ac'
  | 'attack'
  | 'damage'
  | 'initiative'
  | 'spellAttack'
  | 'spellSaveDC'
  | 'hpMax'
  | 'speed'
  | 'passivePerception'
  // Slice 299: `ability` made optional on `save` and `check` (mirror
  // of slice-266 RollTarget wildcard). Canonical user: Stone of Good
  // Luck ("+1 to ability checks and saving throws") wires as two
  // entries (`{save}` + `{check}`) instead of 12 per-ability entries.
  // Sibling cleanups: Cloak/Ring of Protection (6 → 1), blessed /
  // baned / aura-of-protection-active (6 → 1).
  | { kind: 'save'; ability?: AbilityScore }
  | { kind: 'check'; ability?: AbilityScore }
  | { kind: 'skill'; skill: Skill };

export const ModifierTargetSchema: z.ZodType<ModifierTarget> = z.union([
  z.literal('ac'),
  z.literal('attack'),
  z.literal('damage'),
  z.literal('initiative'),
  z.literal('spellAttack'),
  z.literal('spellSaveDC'),
  z.literal('hpMax'),
  z.literal('speed'),
  z.literal('passivePerception'),
  z.object({ kind: z.literal('save'), ability: AbilityScoreSchema.optional() }),
  z.object({ kind: z.literal('check'), ability: AbilityScoreSchema.optional() }),
  z.object({ kind: z.literal('skill'), skill: SkillSchema }),
]);

// Slice 266: `ability` made optional on `save` and `check` so a
// single entry can target "any save" or "any check" (the wildcard).
// Canonical users: poisoned (disadvantage on any check); Mantle of
// Spell Resistance (advantage on any save vs spells). Without the
// wildcard, both needed 6 per-ability entries. The accumulator's
// `advantageFor(target, facts?)` merges wildcard entries with any
// specific-ability query: `advantageFor({kind:'save', ability:'WIS'})`
// picks up both `save:WIS` AND `save:*` entries.
export type RollTarget =
  | 'attack'
  | 'damage'
  | 'initiative'
  | { kind: 'save'; ability?: AbilityScore }
  | { kind: 'check'; ability?: AbilityScore }
  | { kind: 'skill'; skill: Skill };

export const RollTargetSchema: z.ZodType<RollTarget> = z.union([
  z.literal('attack'),
  z.literal('damage'),
  z.literal('initiative'),
  z.object({ kind: z.literal('save'), ability: AbilityScoreSchema.optional() }),
  z.object({ kind: z.literal('check'), ability: AbilityScoreSchema.optional() }),
  z.object({ kind: z.literal('skill'), skill: SkillSchema }),
]);

export type TriggerAction =
  // `cunningStrikeEligible` marks the Sneak Attack damage rider as the one
  // a Rogue's Cunning Strike trades dice from (Rogue L5+). When the attack
  // intent supplies cunning-strike effects, the dispatcher forgoes that
  // many d6 from this rider before rolling and emits the chosen effects.
  | { kind: 'AddDamage'; dice: DiceExpression; damageType: DamageType; cunningStrikeEligible?: boolean }
  // Retaliation variant of AddDamage: emits damage to the attacker of
  // the triggering event instead of the target. Used by Fire Shield's
  // "creature that hits you takes 2d8" rider; the same shape will fit
  // Armor of Agathys (cold retaliation while temp HP > 0) once the
  // bearer-state predicate to gate on temp HP exists. Only fires
  // against AttackRolled triggers (the only event with an attackerId).
  | { kind: 'AddDamageToAttacker'; dice: DiceExpression; damageType: DamageType }
  | { kind: 'Heal'; amount: number | Formula }
  // Slice 349. Grants Temporary Hit Points to the rider's bearer (not
  // the triggering event's target). `amount` is resolved against the
  // bearer's formula context, so it can scale with the bearer's level
  // and ability modifiers. Canonical user: Warlock Fiend Patron's Dark
  // One's Blessing (temp HP = CHA mod + Warlock level, min 1, when the
  // bearer reduces an enemy to 0 HP). Emits a TempHPGranted event,
  // which uses max-not-additive temp-HP semantics (slice 75).
  | { kind: 'GrantTempHP'; amount: number | Formula }
  // Slice 516: emit a `CreaturePushed` event targeting the triggering
  // event's target, pushing them up to `distanceFeet` away from the
  // bearer (the engine doesn't model positions, so the event is
  // informational — consumers apply the position change). Canonical
  // user: Warlock Repelling Blast (Eldritch Blast pushes 10 ft on hit).
  | { kind: 'PushTarget'; distanceFeet: number }
  | { kind: 'ApplyCondition'; conditionId: string; durationRounds?: number }
  // Retaliation variant of ApplyCondition: stamps the condition on
  // the attacker of the triggering event instead of the target. Used
  // by Holy Aura's RAW "fiend or undead that hits you is blinded
  // until the spell ends" rider. Only fires against AttackRolled
  // triggers (the only event with an attackerId). When `durationRounds`
  // is omitted the condition sticks until consumer removes it; when
  // set, slice 102's auto-expiry lifts it at the start of the bearer's
  // turn in the target round.
  //
  // When `sourceFromEventTarget` is true, the emitted ConditionApplied's
  // `sourceCharacterId` is set to the triggering event's `targetId`
  // instead of the rider's bearer. Used by Fighter Studied Attacks
  // (L13): on a miss, the fighter applies a self-condition keyed
  // against the missed creature so SetAdvantageVsSource grants
  // advantage on the fighter's next attack against that same target.
  | { kind: 'ApplyConditionToAttacker'; conditionId: string; durationRounds?: number; sourceFromEventTarget?: boolean }
  | { kind: 'SpendResource'; resourceId: string; amount: number }
  | { kind: 'ModifyDamageTaken'; amount: number | Formula; cap?: number }
  // Slice 233. Spawns a new Character instance from a content
  // statblock when an OnEvent rider fires. Canonical user: Troll's
  // Loathsome Limbs (on slashing damage, spawns a Troll Limb). The
  // statblockId references a monster in the resolved content; the
  // dispatch helper builds a Character from that statblock (HP =
  // statblock average, ability scores + senses + traits copied) and
  // emits a CharacterCreated event for the spawn. Position is
  // narrative (engine doesn't model space). `count` defaults to 1
  // when omitted.
  | { kind: 'SpawnCreature'; statblockId: string; count?: number }
  | { kind: 'EmitEvent'; eventType: string; payload?: unknown };

export const TriggerActionSchema: z.ZodType<TriggerAction> = z.union([
  z.object({
    kind: z.literal('AddDamage'),
    dice: DiceExpressionSchema,
    damageType: DamageTypeSchema,
    cunningStrikeEligible: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('AddDamageToAttacker'),
    dice: DiceExpressionSchema,
    damageType: DamageTypeSchema,
  }),
  z.object({
    kind: z.literal('Heal'),
    amount: z.union([z.number(), FormulaSchema]),
  }),
  z.object({
    kind: z.literal('GrantTempHP'),
    amount: z.union([z.number(), FormulaSchema]),
  }),
  z.object({
    kind: z.literal('PushTarget'),
    distanceFeet: z.number().int().min(0),
  }),
  z.object({
    kind: z.literal('ApplyCondition'),
    conditionId: z.string(),
    durationRounds: z.number().int().optional(),
  }),
  z.object({
    kind: z.literal('ApplyConditionToAttacker'),
    conditionId: z.string(),
    durationRounds: z.number().int().optional(),
    sourceFromEventTarget: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('SpendResource'),
    resourceId: z.string(),
    amount: z.number().int().min(1),
  }),
  z.object({
    kind: z.literal('ModifyDamageTaken'),
    amount: z.union([z.number(), FormulaSchema]),
    cap: z.number().optional(),
  }),
  z.object({
    kind: z.literal('SpawnCreature'),
    statblockId: z.string(),
    count: z.number().int().min(1).optional(),
  }),
  z.object({
    kind: z.literal('EmitEvent'),
    eventType: z.string(),
    payload: z.unknown().optional(),
  }),
]);

export interface ChoiceOptionShape {
  id: string;
  label: string;
  effects: Effect[];
}

export type Effect =
  | { kind: 'GrantProficiency'; target: 'skill' | 'tool' | 'weapon' | 'armor' | 'save' | 'language'; id: string; level: ProficiencyLevel }
  | { kind: 'GrantSense'; sense: Sense; range: number }
  | { kind: 'ModifySpeed'; mode: MovementMode; op: 'set' | 'add' | 'multiply' | 'matchWalkSpeed'; value: number }
  | { kind: 'AddModifier'; target: ModifierTarget; value: number | Formula; condition?: Predicate }
  // Slice 330: a bonus die rolled fresh on each affected roll (RAW
  // "add 1d4 to the attack roll or save", Bless / Bane / Guidance /
  // Resistance / Bardic Inspiration). Distinct from AddModifier's
  // static `value`: the die is rolled in the planner (RNG only lives
  // there) and baked into the emitted roll event, so apply() stays
  // RNG-free. `subtract` flips the sign (Bane). `target` reuses the
  // ModifierTarget vocabulary (attack / { kind: 'save' } / { kind:
  // 'check' } and their per-ability/skill forms).
  | { kind: 'AddBonusDie'; target: ModifierTarget; dice: DiceExpression; subtract?: boolean; condition?: Predicate }
  | { kind: 'SetAdvantage'; on: RollTarget; mode: 'advantage' | 'disadvantage' | 'auto-crit' | 'auto-fail'; condition?: Predicate }
  // Direction-filtered advantage: only applies when the roll's target
  // (e.g. the attack's target creature) equals the bearing applied
  // condition's `sourceCharacterId`. Used by Bestow Curse's "Disadvantage
  // on attack rolls against the caster" variant. Has no effect when the
  // bearing condition has no `sourceCharacterId` (the planner stamps
  // this on spell-applied conditions since slice 88).
  | { kind: 'SetAdvantageVsSource'; on: RollTarget; mode: 'advantage' | 'disadvantage' | 'auto-crit' | 'auto-fail' }
  // Slice 231. Mirror of SetAdvantageVsSource but lives on the
  // attacker (not on a condition on the target). The bearer of this
  // effect has advantage / disadvantage on rolls of `on` whenever
  // the roll's counterparty (e.g. the attack target) carries an
  // active condition with id `conditionId` whose `sourceCharacterId`
  // matches the bearer. Canonical user: Ranger L17 Precise Hunter
  // ("while your Hunter's Mark spell is on a creature, you have
  // Advantage on attack rolls against that creature"). 3-way join:
  // bearer has the marker, target has the named condition, condition's
  // source is the bearer.
  | { kind: 'GrantAdvantageVsBearersOfMyCondition'; conditionId: string; on: RollTarget; mode: 'advantage' | 'disadvantage' | 'auto-crit' | 'auto-fail' }
  // While the bearing condition is active, the bearer cannot regain
  // hit points. Heal planners consult this via the effect stack and,
  // when blocked, emit a Healed event with amount=0 (the reducer's
  // `amount <= 0` early-return makes it a no-op) plus an annotation
  // in the `source` field. Used by Spirit Shroud's "target can't
  // regain HP until start of caster's next turn" rider on a hit;
  // duration is consumer-managed today (no auto-expiry).
  | { kind: 'BlockHealing' }
  // Slice 112: optional `qualifier` restricts the resistance to one
  // damage-source kind. `'nonmagical'` (Stoneskin in SRD form, the
  // common MM "resistance to B/P/S from nonmagical attacks" pattern)
  // applies only when the attacking weapon is not magical and the
  // damage is not spell-sourced. `'magical'` applies only when the
  // damage source IS magical (some monster traits and abjuration
  // effects). When unset the resistance applies regardless of source.
  | { kind: 'GrantResistance'; damageType: DamageType | 'all'; qualifier?: 'nonmagical' | 'magical'; condition?: Predicate }
  | { kind: 'GrantImmunity'; damageType: DamageType | 'all' }
  | { kind: 'GrantVulnerability'; damageType: DamageType }
  | { kind: 'GrantConditionImmunity'; conditionId: string; condition?: Predicate }
  // Slice 131: RAW Magic Resistance. "The creature has advantage on
  // saving throws against spells and other magical effects." Marker-
  // only; consumers (computeSavingThrow) consult the accumulator's
  // hasMagicResistance() and contribute advantage when the save's
  // sourceIsMagical fact is true. Canonical across most CR 5+
  // Fiends / Fey / Dragons / spellcaster monsters.
  | { kind: 'GrantMagicResistance' }
  | { kind: 'OverrideACFormula'; base: number | 'dex' | 'con' | 'wis'; abilityModifiers: AbilityScore[]; dexCap?: number; priority?: number }
  // Sets a floor on the target's AC: after the natural AC is computed
  // from armor + DEX + modifiers, the result is bumped up to `value`
  // if it would otherwise be lower. Used by Barkskin (AC can't be
  // lower than 17 regardless of armor) and similar "minimum AC"
  // effects. Multiple floors fold to the highest. Distinct from
  // OverrideACFormula (which replaces the formula entirely and only
  // applies when unarmored).
  | { kind: 'SetACFloor'; value: number }
  // Slice 229. Floor on an ability score: bumps the bearer's named
  // ability score up to `value` if it would otherwise be lower; has
  // no effect if the bearer's base score is already at or above
  // `value`. Multiple floors on the same ability fold to the highest.
  // Mirrors the SetACFloor shape (slice 74). Canonical users: Amulet
  // of Health (CON 19), Gauntlets of Ogre Power (STR 19), the 6
  // Belt of Giant Strength variants (STR 21/23/25/27/29). Future
  // users: Headband of Intellect, Tome of *, Manual of *.
  | { kind: 'OverrideAbilityScore'; ability: AbilityScore; value: number }
  // Slice 308. Additive ability-score increase capped at `max`,
  // distinct from OverrideAbilityScore (which *sets* a value and would
  // mask a wearer's own higher score). RAW shape: "Your [ability]
  // increases by `amount`, to a maximum of `max`." Canonical users:
  // the six ability Ioun Stones (+2 to max 20) and Belt of
  // Dwarvenkind's Toughness arm (CON +2 to max 20). The increase only
  // raises (never lowers a score that already exceeds `max`); see
  // effectiveAbilityScore. The Manual/Tome variant (permanently raises
  // the score AND its max on a one-time read) is a separate
  // application shape, not this passive-worn projection.
  | { kind: 'IncreaseAbilityScore'; ability: AbilityScore; amount: number; max: number }
  // Slice 232. Monster trait: the bearer regains `perTurn` HP at the
  // start of each of their turns. If they take damage of a type in
  // `suppressedBy` during a turn, regeneration is suppressed on the
  // following turn (the `regenerationSuppressedThisTurn` Character
  // field tracks the per-turn suppression flag). Canonical users:
  // Troll (15 HP/turn, suppressed by Acid/Fire), Troll Limb (5 HP/turn,
  // same suppression). Future users: Werewolf, Hag variants, Vampire.
  // RAW "dies only if it starts its turn with 0 HP and doesn't
  // regenerate" is honored by the Healed-at-turn-start mechanic
  // (regen brings a 0-HP creature back; suppressed + 0 HP falls
  // through to standard unconsciousness rules).
  | { kind: 'Regeneration'; perTurn: number; suppressedBy: DamageType[] }
  | { kind: 'GrantResource'; resourceId: string; max: number | Formula; recharge: Recharge; diceSize?: number }
  | { kind: 'GrantSpellSlots'; level: SpellLevel; count: number; source: 'full' | 'half' | 'third' | 'pact' }
  | { kind: 'GrantSpell'; spellId: string; preparation: 'always-prepared' | 'prepared' | 'known' | 'at-will' | 'oncePerLongRest' | 'oncePerShortRest'; spellcastingAbility?: AbilityScore; freeCastResourceId?: string }
  | { kind: 'OnEvent'; id?: string; trigger: { eventType: string; filter?: Predicate }; actions: TriggerAction[]; oncePer?: 'turn' | 'round' | 'shortRest' | 'longRest'; requiresReaction?: boolean; consumeOnTrigger?: boolean }
  | { kind: 'RecoverResource'; resourceId: string; amount: number | 'all' | Formula; when: 'shortRest' | 'longRest' | 'turnStart' | 'turnEnd' | 'dawn'; limitedByResourceId?: string }
  | { kind: 'GrantAction'; actionId: string; name: string; cost: 'action' | 'bonusAction' | 'reaction' | 'free'; resourceCost?: { resourceId: string; amount: number } }
  | { kind: 'ModifyActionEconomy'; op: 'extraAttack' | 'extraAction' | 'extraBonusAction'; count: number; condition?: Predicate }
  | { kind: 'GrantWeaponMastery'; masteries: WeaponMastery[]; slots: number }
  | { kind: 'ExpandSpellList'; classId: string; spellIds: string[] }
  | { kind: 'SetHPMaxFormula'; formula: Formula }
  | { kind: 'OfferChoice'; choiceId: string; prompt: string; options: ChoiceOptionShape[]; oneOf: number; when: 'onAcquire' | 'onLevelUp' | 'onLongRest'; lifecycle?: 'accumulate' | 'supersede' }
  | { kind: 'FlatDamageReduction'; damageTypes: DamageType[]; amount: number }
  // Lowers the natural-d20 threshold at which the attacker's weapon
  // attacks crit. Default threshold is 20; Improved Critical sets 19,
  // Superior Critical sets 18. Multiple sources stack to the lowest.
  // Does not change auto-hit semantics: only a natural 20 auto-hits.
  | { kind: 'ExpandCritRange'; threshold: number }
  // Bard's Jack of All Trades and similar: any ability check the actor
  // is NOT already proficient in (skill prof level === 'none', or a raw
  // ability check with no skill) gains `floor(profBonus / 2)`. Existing
  // prof / expertise / half-prof are not stacked with.
  | { kind: 'GrantHalfProficiencyBonusFloor' }
  // Cleric's Disciple of Life and similar: boost the HP restored by
  // any healing spell of 1st level or higher cast by this character.
  // Total bonus per heal target is `flat + perSpellLevel * slotLevel`.
  // Cantrips (slotLevel 0) are excluded by the planner that reads this.
  | { kind: 'BoostHealing'; flat: number; perSpellLevel: number }
  // Rogue / Monk Evasion: when this character is forced to make a DEX
  // save against an area-effect spell that ordinarily halves on
  // success, they instead take no damage on success and half damage on
  // failure. Read by `planCastSpell` from the target's effect stack.
  | { kind: 'GrantEvasion' }
  // Evoker L3 Potent Cantrip: the caster's damaging cantrips deal half
  // damage even when the attack roll misses or the target succeeds on the
  // save (and no additional effect). Read by `planCastSpell` from the
  // caster's effect stack.
  | { kind: 'GrantPotentCantrip' }
  // Slice 505: Wizard L1 Ritual Adept. Presence marker for the wizard's
  // "cast spellbook spells as Rituals without preparing them" capability.
  // The cast pathway (`planCastSpell` with `intent.asRitual: true`) is
  // already operationally permissive enough to cover this on its own —
  // `characterKnowsSpell` accepts `knownSpells` (the wizard's spellbook)
  // alone, and `asRitual` skips slot + action consumption. The marker
  // exists so the wire is observable in the effect stack (replacing a
  // misleading `Custom { handlerId: 'ritual-adept' }` content stub) and
  // available to a future RAW-tightening slice that gates ritual casting
  // strictly on a ritual-casting class feature.
  | { kind: 'GrantRitualAdept' }
  // Slice 511: indirection primitive that lets an effect projection
  // include another Feat's effects by id. Canonical user: Warlock
  // Eldritch Invocations — each invocation is authored as a Feat
  // (`category: 'invocation'`), and the warlock's per-tier invocation
  // OfferChoice's options each grant the chosen invocation via
  // `GrantFeat { featId }`. Expanded by `expandGrantFeatEffects` in
  // `src/derive/effect-stack.ts` before reaching `applyEffectToBuilder`
  // (the builder switch sees only fully-expanded leaf effects), with
  // cycle protection. Unlike adding the feat to `character.featsTaken`,
  // GrantFeat does NOT surface the feat in `getEffectiveFeatIds`; it
  // only projects the feat's effects into the effect stack.
  | { kind: 'GrantFeat'; featId: string }
  // Slice 518: Warlock L1 Pact of the Blade invocation presence marker.
  // Read by `planConjurePactWeapon` to gate the conjure action on the
  // bearer having the invocation. The conjure planner takes the
  // weapon-definition choice + optional damage-type override at intent
  // time and stamps an `ItemBuffApplied` on the conjured weapon
  // (abilityOverride 'CHA' + damageTypeOverride; reuses slice-501's
  // `temporaryBuff` shape).
  | { kind: 'GrantPactBlade' }
  // Slice 519 marker. Presence flag indicating the bearer has the Pact
  // of the Chain invocation (warlock). Projected via
  // `EffectAccumulator.markPactChain()` / `hasPactChain()`. Authored
  // alongside a `GrantSpell find-familiar 'at-will'` companion that
  // gives the warlock the free-cast of Find Familiar; this marker is
  // the gate for any future Chain-specific surface (special familiar
  // forms enforcement, the "forgo one attack" reaction arm). Today the
  // special-form list and the familiar attack-via-reaction arm are
  // consumer-managed; the marker exists so a later slice can wire them
  // without changing the feat-side authoring.
  | { kind: 'GrantPactChain' }
  // Slice 662: ability-substitution primitive. RAW shape: "for
  // ability checks using <skill> ∈ skills, you can use <ability>
  // instead of the skill's normal ability — optionally only while
  // a named condition is active." Canonical user: Barbarian L3
  // Primal Knowledge (ability='STR', skills=[acrobatics,
  // intimidation, perception, stealth, survival],
  // activeWhileConditionId='raging'). Read by `planAbilityCheck`
  // from the bearer's effect stack when
  // `intent.useAbilitySubstitution === true`. Replaces the
  // slice-659 hardcoded Primal Knowledge gate.
  | { kind: 'GrantAbilitySubstitution'; ability: AbilityScore; skills: Skill[]; activeWhileConditionId?: string }
  // Slice 538: Halfling Luck. Presence marker for the reroll-on-natural-1
  // mechanic. RAW: "When you roll a 1 on the d20 of a D20 Test, you can
  // reroll the die, and you must use the new roll." Projected via
  // `EffectAccumulator.markHalflingLuck()` / `hasHalflingLuck()`. The
  // attack-roll site reads the accessor and rerolls when the chosen d20
  // is a natural 1. Save / check / other d20 sites stay deferred to
  // follow-up slices that wire the same helper at each site.
  | { kind: 'GrantHalflingLuck' }
  // Slice 678: marker — the bearer's weapon attacks that use STR
  // for the damage ability deal half damage (rounded down). RAW
  // user: the Ray of Enfeeblement spell's `enfeebled` condition.
  // Read by planAttack post-damageTotal computation; the base
  // weapon damage is halved while extra rider damages
  // (sneak attack, smite, on-hit riders, fires-burn, etc.) pass
  // through unhalved. RAW reading scoped to the weapon's damage
  // line, not every damage source the attack triggers.
  | { kind: 'HalvesStrengthWeaponDamage' }
  // Slice 679: marker — bearer's death saving throws are rolled
  // with advantage. RAW user: Beacon of Hope ("It has Advantage
  // on Wisdom saving throws and Death Saving Throws"). Read by
  // `planDeathSaveAtTurnStart` via the character's effect stack;
  // when set, rolls 2d20 and takes the max (RAW advantage).
  | { kind: 'GrantDeathSaveAdvantage' }
  // Slice 542: Heroic Inspiration on Long Rest. RAW: "You gain
  // Heroic Inspiration whenever you finish a Long Rest." Wired
  // on Human Resourceful; future features that grant per-long-
  // rest Inspiration share the same marker. The long-rest planner
  // walks each participant's effect stack for the marker and
  // emits HeroicInspirationGranted.
  | { kind: 'GrantHeroicInspirationOnLongRest' }
  // Cross-character effect: while this is active on a character, attacks
  // against that character are made with advantage. Used by Faerie Fire,
  // Hex (kind of), Hunter's Mark variants. The attack planner consults
  // the *target's* effect stack to pick this up; the attacker doesn't
  // need to know.
  | { kind: 'GrantAdvantageToAttackers'; condition?: Predicate }
  | { kind: 'ImposeDisadvantageOnAttackers'; condition?: Predicate }
  // Slice 199. Marker primitive: while bearing this, any source that
  // would grant an incoming attacker advantage against this character
  // is cancelled. Distinct from `ImposeDisadvantageOnAttackers` — that
  // imposes disadvantage outright (which then cancels with any
  // attacker-side advantage to net "neither"). `CancelAdvantageOnAttackers`
  // suppresses the *advantage* contribution itself, leaving the attack
  // resolved without it (still rolled with whatever disadvantage
  // sources also apply). Canonical user: Rogue L18 Elusive ("No attack
  // roll can have Advantage against you unless you have the
  // Incapacitated condition") via a `bearerHasIncapacitated` predicate
  // that the attack planner populates from `findActorBlockingCondition`.
  // Future users: Wizard / Sorcerer Foresight, similar "advantage
  // immunity" features.
  | { kind: 'CancelAdvantageOnAttackers'; condition?: Predicate }
  // Slice 200. Marker primitive that flags a character as eligible to
  // spend their reaction to halve damage from a hit. Consumed by
  // `planUncannyDodge`, which checks `EffectAccumulator.hasUncannyDodge()`
  // before allowing the reaction. Canonical user: Rogue L5 Uncanny
  // Dodge. The reaction itself is consumer-driven (RAW: "you can
  // take a Reaction") — the engine doesn't auto-fire it; it just
  // gates the planner.
  | { kind: 'GrantUncannyDodge' }
  // Slice 201. Marker primitive that unlocks the Sorcery-Points
  // alternative cost on `planInnateSorcery`. Without the marker, the
  // planner only accepts the innate-sorcery resource path; with it,
  // the consumer may pass `useSorceryPoints: true` to spend 2 SP
  // instead (RAW Sorcery Incarnate L7: "If you have no uses of
  // Innate Sorcery left, you can use it if you spend 2 Sorcery
  // Points..."). Canonical user: Sorcerer L7 Sorcery Incarnate. The
  // double-metamagic arm of the same feature is deferred (needs
  // once-per-spell metamagic enforcement first).
  | { kind: 'GrantInnateSorcerySpendAlternative' }
  // Slice 202. Marker primitive that gates `planSelfRestoration`,
  // RAW Monk L10 Self-Restoration: "you can remove one of the
  // following conditions from yourself at the end of each of your
  // turns: Charmed, Frightened, or Poisoned." The planner removes
  // the named condition unconditionally (no save, no resource cost)
  // when the bearer has the marker. The "food and drink doesn't
  // give Exhaustion" arm of the same feature is consumer-side
  // narrative state (the engine doesn't model food / water).
  | { kind: 'GrantSelfRestoration' }
  // Slice 205. Marker primitive: while the caster bears this, every
  // healing-dice roll in the cast-spell heal-mechanic path is
  // replaced by its maximum value. RAW Life Domain L17 Supreme
  // Healing: "When you would normally roll one or more dice to
  // restore HP with a spell or Channel Divinity, you don't roll
  // those dice; you use the highest possible value instead."
  // Flat modifiers (CHA mod, Disciple of Life boost) compose
  // unchanged on top.
  | { kind: 'GrantMaxHealingDice' }
  // Slice 731. Marker: Cleric Life Domain L6 Blessed Healer — casting a
  // slot spell that heals another creature also heals the caster (2 +
  // slot level). Read by the cast-spell heal handler.
  | { kind: 'GrantBlessedHealer' }
  // Slice 732. Marker: Wizard Evoker L6 Sculpt Spells — when casting an
  // Evocation spell, the caster may exclude up to (1 + slot level)
  // creatures it can see; they auto-succeed on the save and take no
  // damage. Read by `planSaveMechanic` from the caster's effect stack;
  // the excluded targets are named in `intent.sculptedTargetIds`.
  | { kind: 'GrantSculptSpells' }
  // Slice 207. Marker primitive: the bearer's unarmed strikes count
  // as magical attacks for the purposes of overcoming Resistance and
  // Immunity to nonmagical damage. This is the 2014 "Ki-Empowered
  // Strikes" shape; slice 735 moved the SRD 5.2.1 Monk L6 Empowered
  // Strikes off this marker onto `GrantUnarmedForceOption` (the 2024
  // feature is the Force-damage choice, not magical unarmed). Kept as a
  // generic, available primitive (e.g. for magic items / monster traits
  // that grant magical unarmed). `isMagicWeaponAttack` in
  // src/derive/magicality.ts consults this when the weapon's id is
  // `unarmed-strike`. Doesn't change the damage dice or the attack roll,
  // just the `sourceIsMagical` flag passed to `mitigateDamage`.
  | { kind: 'GrantUnarmedAsMagical' }
  // Slice 735. Marker primitive: the bearer may, per unarmed strike,
  // deal Force damage instead of the strike's normal type. RAW SRD 5.2.1
  // Monk L6 Empowered Strikes ("Whenever you deal damage with your
  // Unarmed Strike, it can deal your choice of Force damage or its normal
  // damage type"). Opt-in at attack time: the attack/flurry/off-hand
  // planners read this from the attacker's effect stack and, when the
  // intent sets `unarmedStrikeAsForce` and the weapon is `unarmed-strike`,
  // override the weapon damage type to Force. Without the opt-in the
  // strike keeps its normal type, so the marker is inert by default.
  | { kind: 'GrantUnarmedForceOption' }
  // Slice 738. Marker: Rogue L7 Reliable Talent — on an ability check that
  // uses one of the bearer's skill (or tool) proficiencies, treat a d20 of
  // 9 or lower as a 10. Read by `planAbilityCheck` via the check
  // derivation's `hasReliableTalent` + `usesProficiency` (excludes the
  // half-proficiency floor, which isn't a proficiency the bearer "has").
  | { kind: 'GrantReliableTalent' }
  // Slice 221. Marker primitive that unlocks the Wish branch of
  // `planDivineIntervention`. Without the marker, the planner only
  // accepts Cleric spells of level 5 or lower; with it, the consumer
  // may pass `spellId: 'wish'` even though Wish is level 9 and not
  // on the Cleric list. Canonical user: Cleric L20 Greater Divine
  // Intervention. RAW: "When you use your Divine Intervention
  // feature, you can choose Wish when you select a spell." The
  // 2d4-long-rest cooldown on the Wish branch is a separate concern,
  // pending a `ResourceCooldownExtended` primitive that the rest
  // reducer can honor.
  | { kind: 'GrantDivineInterventionWish' }
  // Slice 211. Additive bonus to the range of every aura the bearer
  // projects. Canonical user: Paladin L18 Aura Expansion (10 ft to
  // 30 ft → addFeet: 20). The engine doesn't auto-project auras
  // (positions are consumer territory) — `GrantAura` ships metadata
  // for consumers to read. This primitive adds an accumulator-side
  // bonus that consumers can read via `effects.auraRangeBonus()` and
  // fold into the displayed / enforced aura range.
  | { kind: 'ExpandAuraRange'; addFeet: number }
  // A "this character projects an aura" marker. Auras are inherently
  // position-dependent (RAW typically "creatures within X feet"), and
  // the engine doesn't model continuous position. So this effect is
  // metadata-only: the consumer (dndbnb / DM tool / VTT) reads the
  // bearer's `GrantAura` effects, decides which characters are
  // currently in range using whatever position model it tracks, and
  // applies / removes the `allyConditionId` on those characters via
  // `ConditionApplied` / `ConditionRemoved` events. The bearer's own
  // self-effect (e.g. Aura of Courage also making the bearer Frightened-
  // immune) ships as a separate sibling effect on the same feature.
  | { kind: 'GrantAura'; auraId: string; rangeFeet: number; allyConditionId: string; description?: string }
  // Negates fall damage entirely (Feather Fall, Monk Slow Fall at
  // high tiers, similar). Distinct from damage-type immunity because
  // falling isn't a damage type — it's a damage source. planFalling
  // consults this when computing fall damage; presence zeroes the
  // outgoing DamageApplied.
  | { kind: 'GrantFallingProtection' }
  // Slice 111. When the bearing condition lives on a creature and
  // incoming damage would drop HP to 0 or below, the damage is
  // reduced so HP lands at 1 and the bearing condition is removed.
  // Canonical user: Death Ward. Future users: Half-Orc Relentless
  // Endurance, Aura of Life.
  | { kind: 'PreventFatalDamage' }
  // Slice 456. Save-gated fatal-damage prevention. When incoming damage
  // would drop HP to 0 or below, the bearer rolls a save (`ability` vs
  // `baseDC` + total damage taken); on success, damage is scaled so HP
  // lands at 1. RAW exemptions: `exemptDamageTypes` (skip the save entirely
  // if any damage component is one of these — Undead Fortitude exempts
  // Radiant) and `exemptOnCrit` (skip the save if the triggering attack was
  // a critical hit — Undead Fortitude exempts crits). The bearing
  // condition / trait is NOT removed on success (unlike PreventFatalDamage):
  // Undead Fortitude is an always-on monster trait, not a one-shot.
  // Canonical user: Zombie Undead Fortitude (CON, DC 5, exemptDamageTypes
  // ['radiant'], exemptOnCrit true). Same shape would fit Half-Orc
  // Relentless Endurance (1/long-rest gate, not crit-exempt).
  | {
      kind: 'PreventFatalDamageOnSave';
      ability: AbilityScore;
      baseDC: number;
      exemptDamageTypes?: DamageType[];
      exemptOnCrit?: boolean;
    }
  // Slice 458. Resource-gated fatal-damage prevention. When incoming
  // damage would drop HP to 0 or below AND the bearer has at least 1
  // of `resourceId` remaining, scale damage so HP lands at 1 and emit
  // a ResourceSpent for 1. The trait stays projected on the effect
  // stack (it's species-built-in, not a one-shot condition); the per-
  // long-rest semantic comes from the resource's own recharge cadence.
  // Canonical user: Orc Relentless Endurance (resourceId
  // 'relentless-endurance', max 1, recharge longRest).
  | { kind: 'PreventFatalDamageConsumingResource'; resourceId: string }
  // Slice 119. Marker that enables the Two-Weapon Fighting bonus:
  // planOffHandAttack adds the wielder's ability modifier to off-hand
  // damage (positive mods included). Without this flag, off-hand
  // damage only includes negative ability mods per RAW. Canonical
  // user: the Two-Weapon Fighting Fighting Style.
  | { kind: 'GrantTwoWeaponFighting' }
  // Slice 120. Marker that enables the Protection reaction:
  // planProtection consults the bearer's effect stack for this flag
  // before letting them spend their reaction to impose disadvantage on
  // an attack against a nearby ally. The shield requirement is checked
  // separately (character.equipped.shield !== undefined). Canonical
  // user: the Protection Fighting Style.
  | { kind: 'GrantProtectionFightingStyle' }
  // Slice 121. Marker that enables Great Weapon Fighting's reroll
  // rule: planAttack replaces any 1 or 2 on a weapon damage die with
  // 3, but only when the attack is melee and the weapon is wielded
  // two-handed (Two-Handed property, or Versatile property with both
  // off-hand and shield slots empty). Canonical user: the Great
  // Weapon Fighting Fighting Style.
  | { kind: 'GrantGreatWeaponFighting' }
  | { kind: 'Custom'; handlerId: string; params?: unknown };

export const EffectSchema: z.ZodType<Effect> = z.lazy(() =>
  z.union([
    z.object({
      kind: z.literal('GrantProficiency'),
      target: z.enum(['skill', 'tool', 'weapon', 'armor', 'save', 'language']),
      id: z.string(),
      level: ProficiencyLevelSchema,
    }),
    z.object({
      kind: z.literal('GrantSense'),
      sense: SenseSchema,
      range: z.number().int().min(0),
    }),
    z.object({
      kind: z.literal('ModifySpeed'),
      mode: MovementModeSchema,
      op: z.enum(['set', 'add', 'multiply', 'matchWalkSpeed']),
      // `value` is unused for `op: 'matchWalkSpeed'` (the entry says
      // "set this mode's speed to the bearer's effective walk speed").
      // Kept required across all ops to avoid a discriminated-union
      // expansion; `matchWalkSpeed` entries should ship `value: 0`.
      value: z.number(),
    }),
    z.object({
      kind: z.literal('AddModifier'),
      target: ModifierTargetSchema,
      value: z.union([z.number(), FormulaSchema]),
      condition: PredicateSchema.optional(),
    }),
    z.object({
      kind: z.literal('AddBonusDie'),
      target: ModifierTargetSchema,
      dice: DiceExpressionSchema,
      subtract: z.boolean().optional(),
      condition: PredicateSchema.optional(),
    }),
    z.object({
      kind: z.literal('SetAdvantage'),
      on: RollTargetSchema,
      mode: z.enum(['advantage', 'disadvantage', 'auto-crit', 'auto-fail']),
      condition: PredicateSchema.optional(),
    }),
    z.object({
      kind: z.literal('SetAdvantageVsSource'),
      on: RollTargetSchema,
      mode: z.enum(['advantage', 'disadvantage', 'auto-crit', 'auto-fail']),
    }),
    z.object({
      kind: z.literal('GrantAdvantageVsBearersOfMyCondition'),
      conditionId: z.string(),
      on: RollTargetSchema,
      mode: z.enum(['advantage', 'disadvantage', 'auto-crit', 'auto-fail']),
    }),
    z.object({
      kind: z.literal('BlockHealing'),
    }),
    z.object({
      kind: z.literal('GrantResistance'),
      damageType: z.union([DamageTypeSchema, z.literal('all')]),
      qualifier: z.enum(['nonmagical', 'magical']).optional(),
      condition: PredicateSchema.optional(),
    }),
    z.object({
      kind: z.literal('GrantImmunity'),
      damageType: z.union([DamageTypeSchema, z.literal('all')]),
    }),
    z.object({
      kind: z.literal('GrantVulnerability'),
      damageType: DamageTypeSchema,
    }),
    z.object({
      kind: z.literal('GrantConditionImmunity'),
      conditionId: z.string(),
      condition: PredicateSchema.optional(),
    }),
    z.object({
      kind: z.literal('GrantMagicResistance'),
    }),
    z.object({
      kind: z.literal('OverrideACFormula'),
      base: z.union([z.number(), z.literal('dex'), z.literal('con'), z.literal('wis')]),
      abilityModifiers: z.array(AbilityScoreSchema),
      dexCap: z.number().optional(),
      priority: z.number().int().optional(),
    }),
    z.object({
      kind: z.literal('OverrideAbilityScore'),
      ability: AbilityScoreSchema,
      value: z.number().int().min(1).max(30),
    }),
    z.object({
      kind: z.literal('IncreaseAbilityScore'),
      ability: AbilityScoreSchema,
      amount: z.number().int().min(1),
      max: z.number().int().min(1).max(30),
    }),
    z.object({
      kind: z.literal('Regeneration'),
      perTurn: z.number().int().min(1),
      suppressedBy: z.array(DamageTypeSchema),
    }),
    z.object({
      kind: z.literal('SetACFloor'),
      value: z.number().int().min(1),
    }),
    z.object({
      kind: z.literal('GrantResource'),
      resourceId: z.string(),
      max: z.union([z.number().int().min(0), FormulaSchema]),
      recharge: RechargeSchema,
      diceSize: z.number().int().optional(),
      // Slice 613: optional human-readable label for the resource, used
      // by user-facing surfaces (transcript formatter, web demo) so the
      // ResourceSpent line reads "spends 1 Relentless Endurance" not
      // "spends 1 relentless-endurance". Decouples slugs (machine ids)
      // from display strings. Unset → consumers fall back to a
      // title-cased version of the slug.
      label: z.string().optional(),
    }),
    z.object({
      kind: z.literal('GrantSpellSlots'),
      level: SpellLevelSchema,
      count: z.number().int().min(0),
      source: z.enum(['full', 'half', 'third', 'pact']),
    }),
    z.object({
      kind: z.literal('GrantSpell'),
      spellId: z.string(),
      preparation: z.enum([
        'always-prepared',
        'prepared',
        'known',
        'at-will',
        'oncePerLongRest',
        'oncePerShortRest',
      ]),
      spellcastingAbility: AbilityScoreSchema.optional(),
      // Slice 566: pool-based free-cast bypass. When set, a cast of
      // `spellId` with `useFreeCast: true` consumes 1 from the named
      // resource (emits ResourceSpent) and skips the slot. Composes
      // with `preparation: 'always-prepared'` for the canonical RAW
      // shape "always prepared + N free casts per long rest": Ranger
      // Favored Enemy grants Hunter's Mark always-prepared with
      // `freeCastResourceId: 'hunters-mark'`, and a sibling
      // GrantResource pumps the resource's max (2 at L1, 3/4/5/6 at
      // L5/9/13/17) under the SAME `recharge: 'longRest'` policy.
      freeCastResourceId: z.string().optional(),
    }),
    z.object({
      kind: z.literal('OnEvent'),
      id: z.string().optional(),
      trigger: z.object({
        eventType: z.string(),
        filter: PredicateSchema.optional(),
      }),
      actions: z.array(TriggerActionSchema),
      oncePer: z.enum(['turn', 'round', 'shortRest', 'longRest']).optional(),
      requiresReaction: z.boolean().optional(),
      consumeOnTrigger: z.boolean().optional(),
    }),
    z.object({
      kind: z.literal('RecoverResource'),
      resourceId: z.string(),
      amount: z.union([z.number(), z.literal('all'), FormulaSchema]),
      when: z.enum(['shortRest', 'longRest', 'turnStart', 'turnEnd', 'dawn']),
      // Slice 718: a once-per-X gate. When set, the recovery applies only
      // if the character's `limitedByResourceId` resource is available
      // (current > 0), and spends 1 of it (Sorcerous Restoration: gated by
      // a `sorcerous-restoration` resource that recharges on a Long Rest,
      // so the recovery fires at most once per Long Rest).
      limitedByResourceId: z.string().optional(),
    }),
    z.object({
      kind: z.literal('GrantAction'),
      actionId: z.string(),
      name: z.string(),
      cost: z.enum(['action', 'bonusAction', 'reaction', 'free']),
      resourceCost: z
        .object({ resourceId: z.string(), amount: z.number().int().min(1) })
        .optional(),
    }),
    z.object({
      kind: z.literal('ModifyActionEconomy'),
      op: z.enum(['extraAttack', 'extraAction', 'extraBonusAction']),
      count: z.number().int().min(1),
      condition: PredicateSchema.optional(),
    }),
    z.object({
      kind: z.literal('GrantWeaponMastery'),
      masteries: z.array(WeaponMasterySchema),
      slots: z.number().int().min(1),
    }),
    z.object({
      kind: z.literal('ExpandSpellList'),
      classId: z.string(),
      spellIds: z.array(z.string()),
    }),
    z.object({
      kind: z.literal('SetHPMaxFormula'),
      formula: FormulaSchema,
    }),
    z.object({
      kind: z.literal('OfferChoice'),
      choiceId: z.string(),
      prompt: z.string(),
      options: z
        .array(
          z.object({
            id: z.string(),
            label: z.string(),
            effects: z.array(EffectSchema),
          }),
        )
        .min(1),
      oneOf: z.number().int().min(1),
      when: z.enum(['onAcquire', 'onLevelUp', 'onLongRest']),
      // Slice 661: when 'supersede', only the LATEST resolution of
      // this choiceId contributes effects in the effect-stack derive
      // (older resolutions stay in the event log for replay but
      // their granted effects are dropped). Canonical user:
      // Druid Circle of the Land Spells (RAW: each long rest the
      // druid picks a land and the prior land's spells go away).
      // Default 'accumulate' preserves slice-618 OfferChoice
      // behavior (every resolved choice contributes).
      lifecycle: z.enum(['accumulate', 'supersede']).optional(),
    }),
    z.object({
      kind: z.literal('FlatDamageReduction'),
      damageTypes: z.array(DamageTypeSchema).min(1),
      amount: z.number().int().min(1),
    }),
    z.object({
      kind: z.literal('ExpandCritRange'),
      threshold: z.number().int().min(2).max(20),
    }),
    z.object({
      kind: z.literal('GrantHalfProficiencyBonusFloor'),
    }),
    z.object({
      kind: z.literal('BoostHealing'),
      flat: z.number().int(),
      perSpellLevel: z.number().int(),
    }),
    z.object({
      kind: z.literal('GrantEvasion'),
    }),
    z.object({
      kind: z.literal('GrantPotentCantrip'),
    }),
    z.object({
      kind: z.literal('GrantRitualAdept'),
    }),
    z.object({
      kind: z.literal('GrantFeat'),
      featId: z.string(),
    }),
    z.object({
      kind: z.literal('GrantPactBlade'),
    }),
    z.object({
      kind: z.literal('GrantPactChain'),
    }),
    z.object({
      kind: z.literal('GrantAbilitySubstitution'),
      ability: AbilityScoreSchema,
      skills: z.array(SkillSchema).min(1),
      // When set, the substitution only applies while the bearer
      // has the named condition active. RAW user: Primal
      // Knowledge requires 'raging'. Future generic users may
      // omit this for unconditional substitutions.
      activeWhileConditionId: z.string().optional(),
    }),
    z.object({
      kind: z.literal('GrantHalflingLuck'),
    }),
    z.object({
      kind: z.literal('HalvesStrengthWeaponDamage'),
    }),
    z.object({
      kind: z.literal('GrantDeathSaveAdvantage'),
    }),
    z.object({
      kind: z.literal('GrantHeroicInspirationOnLongRest'),
    }),
    z.object({
      kind: z.literal('GrantAdvantageToAttackers'),
      condition: PredicateSchema.optional(),
    }),
    z.object({
      kind: z.literal('ImposeDisadvantageOnAttackers'),
      condition: PredicateSchema.optional(),
    }),
    z.object({
      kind: z.literal('CancelAdvantageOnAttackers'),
      condition: PredicateSchema.optional(),
    }),
    z.object({
      kind: z.literal('GrantUncannyDodge'),
    }),
    z.object({
      kind: z.literal('GrantInnateSorcerySpendAlternative'),
    }),
    z.object({
      kind: z.literal('GrantSelfRestoration'),
    }),
    z.object({
      kind: z.literal('GrantMaxHealingDice'),
    }),
    z.object({
      kind: z.literal('GrantBlessedHealer'),
    }),
    z.object({
      kind: z.literal('GrantSculptSpells'),
    }),
    z.object({
      kind: z.literal('GrantUnarmedAsMagical'),
    }),
    z.object({
      kind: z.literal('GrantUnarmedForceOption'),
    }),
    z.object({
      kind: z.literal('GrantReliableTalent'),
    }),
    z.object({
      kind: z.literal('GrantDivineInterventionWish'),
    }),
    z.object({
      kind: z.literal('ExpandAuraRange'),
      addFeet: z.number().int().min(0),
    }),
    z.object({
      kind: z.literal('GrantAura'),
      auraId: z.string(),
      rangeFeet: z.number().int().min(0),
      allyConditionId: z.string(),
      description: z.string().optional(),
    }),
    z.object({
      kind: z.literal('GrantFallingProtection'),
    }),
    z.object({
      // Slice 111. Marker primitive. When the bearing condition lives
      // on a creature and incoming damage would drop their HP to 0 or
      // below, the damage is reduced so HP lands at 1 and the bearing
      // condition is removed. Canonical user: Death Ward (PHB 2024).
      // Future users: Half-Orc Relentless Endurance, Aura of Life
      // floor, Beacon of Hope's death-save advantage variant.
      kind: z.literal('PreventFatalDamage'),
    }),
    z.object({
      // Slice 456. Save-gated fatal-damage prevention (Undead Fortitude
      // shape). See the corresponding union type comment above.
      kind: z.literal('PreventFatalDamageOnSave'),
      ability: AbilityScoreSchema,
      baseDC: z.number().int().min(0),
      exemptDamageTypes: z.array(DamageTypeSchema).optional(),
      exemptOnCrit: z.boolean().optional(),
    }),
    z.object({
      // Slice 458. Resource-gated fatal-damage prevention (Orc
      // Relentless Endurance shape). See the corresponding union type
      // comment above.
      kind: z.literal('PreventFatalDamageConsumingResource'),
      resourceId: z.string(),
    }),
    z.object({
      kind: z.literal('GrantTwoWeaponFighting'),
    }),
    z.object({
      kind: z.literal('GrantProtectionFightingStyle'),
    }),
    z.object({
      kind: z.literal('GrantGreatWeaponFighting'),
    }),
    z.object({
      kind: z.literal('Custom'),
      handlerId: z.string(),
      params: z.unknown().optional(),
    }),
  ]),
);

export type EffectKind = Effect['kind'];

export const EFFECT_KINDS = [
  'GrantProficiency',
  'GrantSense',
  'ModifySpeed',
  'AddModifier',
  'AddBonusDie',
  'SetAdvantage',
  'SetAdvantageVsSource',
  'GrantAdvantageVsBearersOfMyCondition',
  'BlockHealing',
  'GrantResistance',
  'GrantImmunity',
  'GrantVulnerability',
  'GrantConditionImmunity',
  'GrantMagicResistance',
  'OverrideACFormula',
  'OverrideAbilityScore',
  'IncreaseAbilityScore',
  'Regeneration',
  'SetACFloor',
  'GrantResource',
  'GrantSpellSlots',
  'GrantSpell',
  'OnEvent',
  'RecoverResource',
  'GrantAction',
  'ModifyActionEconomy',
  'GrantWeaponMastery',
  'ExpandSpellList',
  'SetHPMaxFormula',
  'OfferChoice',
  'FlatDamageReduction',
  'ExpandCritRange',
  'GrantHalfProficiencyBonusFloor',
  'BoostHealing',
  'GrantEvasion',
  'GrantPotentCantrip',
  'GrantRitualAdept',
  'GrantFeat',
  'GrantPactBlade',
  'GrantPactChain',
  'GrantAbilitySubstitution',
  'GrantHalflingLuck',
  'HalvesStrengthWeaponDamage',
  'GrantDeathSaveAdvantage',
  'GrantHeroicInspirationOnLongRest',
  'GrantAdvantageToAttackers',
  'ImposeDisadvantageOnAttackers',
  'CancelAdvantageOnAttackers',
  'GrantUncannyDodge',
  'GrantInnateSorcerySpendAlternative',
  'GrantSelfRestoration',
  'GrantMaxHealingDice',
  'GrantBlessedHealer',
  'GrantSculptSpells',
  'GrantUnarmedAsMagical',
  'GrantUnarmedForceOption',
  'GrantReliableTalent',
  'GrantDivineInterventionWish',
  'ExpandAuraRange',
  'GrantAura',
  'GrantFallingProtection',
  'PreventFatalDamage',
  'PreventFatalDamageOnSave',
  'PreventFatalDamageConsumingResource',
  'GrantTwoWeaponFighting',
  'GrantProtectionFightingStyle',
  'GrantGreatWeaponFighting',
  'Custom',
] as const satisfies ReadonlyArray<EffectKind>;
